import React from 'react';
import { Client, ClientIcon, dayjs, FilterPagination, GroupedOption, ListPaginationResult, MultiSelect, Option, Sector, SectorIcon, Site, SiteIcon, ThemeIcon } from '@umanforge/common';
import { EventManagerIcon } from '@common_components/icons/eventManager.icon';
import { UserIcon } from '@common_components/icons/user.icon';
import DatePickerInput from '@common_components/inputs/datePickerInput.component';
import { ApiTheme } from 'interfaces/theme.interface';
import { AdminTableWithSitesApiResponse, Participant } from 'interfaces/eventManager.interface';
import { useTranslation } from 'react-i18next';

interface ExtractionFiltersPanelProps {
  filterWidthClass: string;
  clientsOption: Option<Client>[];
  sitesOption: GroupedOption<Site>[];
  sectorsOption: GroupedOption<Sector>[];
  eventManagersOption: GroupedOption<AdminTableWithSitesApiResponse>[];
  participantsOptions: GroupedOption<Participant>[] | ( ( filter: FilterPagination ) => Promise<ListPaginationResult<Participant>> );
  themesOptions: GroupedOption<ApiTheme>[];
  creatorsOptions: GroupedOption<Participant>[] | ( ( filter: FilterPagination ) => Promise<ListPaginationResult<Participant>> );
  selectedClients: Option<Client>[];
  selectedSites: Option<Site>[];
  selectedSectors: Option<Sector>[];
  selectedEventManagers: Option<AdminTableWithSitesApiResponse>[];
  selectedParticipants: Option<Participant>[];
  selectedThemes: Option<ApiTheme>[];
  selectedCreators: Option<Participant>[];
  setSelectedClients: ( value: Option<Client>[] ) => void;
  setSelectedSites: ( value: Option<Site>[] ) => void;
  setSelectedSectors: ( value: Option<Sector>[] ) => void;
  setSelectedEventManagers: ( value: Option<AdminTableWithSitesApiResponse>[] ) => void;
  setSelectedParticipants: ( value: Option<Participant>[] ) => void;
  setSelectedThemes: ( value: Option<ApiTheme>[] ) => void;
  setSelectedCreators: ( value: Option<Participant>[] ) => void;
  creationDate: Date[];
  deliveryDate: Date[];
  archivedDate: Date[];
  completionDate: Date[];
  setCreationDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
  setDeliveryDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
  setArchivedDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
  setCompletionDateFunct: ( startDate: Date | null, endDate: Date | null ) => void;
}

const ExtractionFiltersPanel: React.FC<ExtractionFiltersPanelProps> = ( {
  filterWidthClass,
  clientsOption,
  sitesOption,
  sectorsOption,
  eventManagersOption,
  participantsOptions,
  themesOptions,
  creatorsOptions,
  selectedClients,
  selectedSites,
  selectedSectors,
  selectedEventManagers,
  selectedParticipants,
  selectedThemes,
  selectedCreators,
  setSelectedClients,
  setSelectedSites,
  setSelectedSectors,
  setSelectedEventManagers,
  setSelectedParticipants,
  setSelectedThemes,
  setSelectedCreators,
  creationDate,
  deliveryDate,
  archivedDate,
  completionDate,
  setCreationDateFunct,
  setDeliveryDateFunct,
  setArchivedDateFunct,
  setCompletionDateFunct,
} ) => {
  const { t } = useTranslation();

  return (
    <div className={`flex flex-col shrink-0 ${filterWidthClass} gap-2`}>
      {
        clientsOption.length > 1 && (
          <MultiSelect
            label={t( 'common:clients' )}
            icon={<ClientIcon className="text-orange-base" />}
            placeholder={ t( 'common:search' ) }
            getValues={ clientsOption }
            selectedValues={ selectedClients }
            onChange={ setSelectedClients }
          />
        )
      }
      {
        ( clientsOption.length > 1 || sitesOption.map( item => { return item.options; } ).flat().length > 1 ) && (
          <MultiSelect
            label={t( 'common:sites' )}
            icon={<SiteIcon className="text-orange-base" />}
            placeholder={ t( 'common:search' ) }
            getValues={ sitesOption }
            selectedValues={ selectedSites }
            onChange={ setSelectedSites }
          />
        )
      }
      {
        ( clientsOption.length > 1 || sitesOption.map( item => { return item.options; } ).flat().length > 1 || sectorsOption.map( item => { return item.options; } ).flat().length > 1 ) && (
          <MultiSelect
            label={t( 'common:sectors' )}
            icon={<SectorIcon className="text-orange-base" />}
            placeholder={ t( 'common:search' ) }
            getValues={ sectorsOption }
            selectedValues={ selectedSectors }
            onChange={ setSelectedSectors }
          />
        )
      }

      <MultiSelect
        label={t( 'translation:event_manager' )}
        icon={<EventManagerIcon className="text-orange-base" />}
        placeholder={ t( 'common:search' ) }
        getValues={ eventManagersOption }
        selectedValues={ selectedEventManagers }
        onChange={ setSelectedEventManagers }
      />

      <MultiSelect
        label={t( 'common:participant' )}
        icon={<UserIcon className="text-orange-base" />}
        placeholder={ t( 'common:search' ) }
        getValues={ participantsOptions }
        selectedValues={ selectedParticipants }
        onChange={ setSelectedParticipants }
      />

      <MultiSelect
        label={t( 'common:theme' )}
        icon={<ThemeIcon className="text-orange-base" />}
        placeholder={ t( 'common:search' ) }
        getValues={ themesOptions }
        selectedValues={ selectedThemes }
        onChange={ setSelectedThemes }
      />

      <MultiSelect
        label={t( 'common:author' )}
        icon={<UserIcon className="text-orange-base" />}
        placeholder={ t( 'common:search' ) }
        getValues={ creatorsOptions }
        selectedValues={ selectedCreators }
        onChange={ setSelectedCreators }
      />

      <DatePickerInput
        textSize='w-full'
        label={t( 'common:creation_date' )}
        placeholder={t( 'translation:select_date' )}
        onChange={setCreationDateFunct}
        startDate={ creationDate.length === 2 ? creationDate[ 0 ] : null }
        endDate={ creationDate.length === 2 ? creationDate[ 1 ] : null }
        customButtons={ [ { label: t( 'common:clear' ), dates: null } ] }
        labelPosition='top'
        startMounthToSee={ dayjs().add( -1, 'month' ).startOf( 'month' ).toDate() }
      />

      <DatePickerInput
        textSize='w-full'
        label={t( 'translation:engagement_date' )}
        placeholder={t( 'translation:select_date' )}
        onChange={setDeliveryDateFunct}
        startDate={ deliveryDate.length === 2 ? deliveryDate[ 0 ] : null }
        endDate={ deliveryDate.length === 2 ? deliveryDate[ 1 ] : null }
        customButtons={ [ { label: t( 'common:clear' ), dates: null } ] }
        labelPosition='top'
      />

      <DatePickerInput
        textSize='w-full'
        placeholder={t( 'translation:select_date' )}
        label={t( 'common:archived_date' )}
        onChange={setArchivedDateFunct}
        startDate={ archivedDate.length === 2 ? archivedDate[ 0 ] : null }
        endDate={ archivedDate.length === 2 ? archivedDate[ 1 ] : null }
        customButtons={ [ { label: t( 'common:clear' ), dates: null } ] }
        labelPosition='top'
        startMounthToSee={ dayjs().add( -1, 'month' ).startOf( 'month' ).toDate() }
      />

      <DatePickerInput
        textSize='w-full'
        placeholder={t( 'translation:select_date' )}
        label={t( 'common:completion_date' )}
        onChange={setCompletionDateFunct}
        startDate={ completionDate.length === 2 ? completionDate[ 0 ] : null }
        endDate={ completionDate.length === 2 ? completionDate[ 1 ] : null }
        customButtons={ [ { label: t( 'common:clear' ), dates: null } ] }
        labelPosition='top'
        startMounthToSee={ dayjs().add( -1, 'month' ).startOf( 'month' ).toDate() }
      />
    </div>
  );
};

export default ExtractionFiltersPanel;
